import React, { useCallback, useEffect, useMemo, useState } from "react"
import { ParsedUrlQuery } from "querystring"
import { GetServerSideProps } from "next"
import { AxiosError } from "axios"

import { WorkItem } from "../../models/WorkItem/WorkItem"
import { DefaultLayoutForReader } from "src/components/common/layouts/DefaultLayoutForReader"
import { useRouter } from "next/router"
import { useToggleR18 } from "src/components/hooks/useToggleR18"
import HTMLHead from "src/components/utils/HTMLHead"
import { useInfinityLoad } from "../../components/hooks/useInfinityLoad"
import { fetchTieupRanking } from "../../models/tieup/ranking/fetchTieupRanking"
import { RankingBody } from "../../components/pages/ranking/RankingBody"
import { useTieupToast } from "../../components/hooks/useTieupToast"

const LIMIT = 20
const MAX_SIZE = 30

type Category = "total" | "monthly"

type Props = {
  _category?: Category
}

const getCategoryFromQuery = (query: ParsedUrlQuery) => {
  if (query && query.category && query.category == "total") {
    return query.category
  }

  // クエリパラメータが無ければデフォルトの月間(monthly)を返す
  return "monthly"
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  return {
    props: {
      _category: getCategoryFromQuery(query),
    },
  }
}

const Ranking: React.FC<Props> = ({ _category }) => {
  const router = useRouter()
  const { showR18, toggleShowR18 } = useToggleR18()
  const [category, setCategory] = useState(_category)
  const { addToastError } = useTieupToast()

  const fetcherKey = useMemo(() => {
    const params = new URLSearchParams({
      category: category as string,
      showR18: showR18 ? "yes" : "no",
    })

    return `/v1/tieup/ranking?${params.toString()}`
  }, [category, showR18])

  const fetcher = useCallback(
    (pageIndex: number) => {
      const offset = pageIndex * LIMIT
      const limit = offset + LIMIT >= MAX_SIZE ? MAX_SIZE - offset : LIMIT

      return fetchTieupRanking(
        category as Category,
        showR18 || false,
        limit,
        offset,
      ).then((v) => {
        // 1ページの表示数が決まっているので
        return {
          total: v.total,
          items: v.items,
        }
      })
    },
    [category, showR18],
  )

  const { list, error, loadMore, canLoadMore, isLoading, resetIndex } =
    useInfinityLoad<WorkItem, AxiosError>({
      fetcher,
      fetcherKey,
      displayCount: LIMIT,
      maxLength: MAX_SIZE,
    })

  /** 18禁コンテンツの表示 / 非表示 */
  const handleChangeR18Toggle = useCallback(() => {
    toggleShowR18()
    resetIndex()
  }, [toggleShowR18, resetIndex])

  const onChangeCategory = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as Category
      setCategory(value)
      resetIndex()
      router.push(`/ranking?category=${value}`)
    },
    [router, resetIndex],
  )

  const onLoadMore = useCallback(() => {
    if (isLoading) {
      return
    }

    loadMore()
  }, [isLoading, loadMore])

  useEffect(() => {
    if (error === undefined) {
      return
    }
    addToastError(
      "ランキングの取得に失敗しました。暫く後でお試し下さい",
      false,
      "fetchTieupRankingError",
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  return (
    <DefaultLayoutForReader>
      <HTMLHead path="ranking" />
      <main className="p-ranking">
        <div className="l-single">
          {/* ヘッダー */}
          <header className="p-ranking-header">
            <div className="p-ranking-header-inner">
              <div className="p-ranking-col01">
                <h1 className="p-ranking-title">ランキング一覧</h1>
              </div>

              <div className="p-ranking-col02">
                <div className="p-ranking-select">
                  <div className="p-ranking-select-inner c-select-primary">
                    <select
                      className="c-select-primary-body"
                      value={category}
                      onChange={onChangeCategory}
                    >
                      <option value="monthly">月間</option>
                      <option value="total">総合</option>
                    </select>
                  </div>
                </div>
                <label className="p-ranking-toggle">
                  <span className="p-ranking-toggle-label">
                    18禁コンテンツの表示
                  </span>
                  <input
                    className="p-ranking-toggle-input"
                    type="checkbox"
                    name="18禁コンテンツの表示"
                    value="18禁コンテンツを表示する"
                    onChange={() => handleChangeR18Toggle()}
                    checked={showR18}
                  />
                  <span className="p-ranking-toggle-button"></span>
                </label>
              </div>
            </div>
          </header>

          {/* コンテンツ */}
          <RankingBody
            items={list}
            canLoadMore={canLoadMore}
            onLoadMore={onLoadMore}
            isLoading={isLoading}
          />
        </div>
      </main>
    </DefaultLayoutForReader>
  )
}

export default Ranking
